import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, 
  Code2, 
  Mail, 
  Zap, 
  ArrowRight, 
  CheckCircle, 
  AlertCircle,
  Play,
  Settings,
  Eye
} from "lucide-react";

interface TemplateGettingStartedProps {
  onCreateTemplate: () => void;
  onViewExisting: () => void;
}

export function TemplateGettingStarted({ onCreateTemplate, onViewExisting }: TemplateGettingStartedProps) {
  return (
    <div className="space-y-6">
      <Alert className="border-blue-200 bg-blue-50 text-blue-800">
        <BookOpen className="h-4 w-4" />
        <AlertDescription>
          <div className="flex items-center justify-between">
            <div>
              <strong>Welcome to React Email Templates!</strong>
              <p className="text-sm mt-1">
                Create professional email templates with React components and dynamic Handlebars variables.
              </p>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button size="sm" onClick={onViewExisting}>
                <Eye className="h-4 w-4 mr-2" />
                View Templates
              </Button>
              <Button size="sm" variant="outline" onClick={onCreateTemplate}>
                <Code2 className="h-4 w-4 mr-2" />
                Create New
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Play className="h-5 w-5 text-green-600" />
              Quick Start Guide
            </CardTitle>
            <CardDescription>
              Get started with React Email templates in minutes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <div className="font-medium">Choose Template Format</div>
                  <div className="text-sm text-muted-foreground">Select "React Email" for professional templates</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <div className="font-medium">Configure Template</div>
                  <div className="text-sm text-muted-foreground">Set name, category, and React component</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <div className="font-medium">Add Variables</div>
                  <div className="text-sm text-muted-foreground">Define Handlebars variables for dynamic content</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <div className="font-medium">Test & Deploy</div>
                  <div className="text-sm text-muted-foreground">Preview and activate your template</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="h-5 w-5 text-blue-600" />
              Available Templates
            </CardTitle>
            <CardDescription>
              Pre-built templates ready for customization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Booking Confirmation</span>
                </div>
                <Badge variant="secondary">Active</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Payment Confirmation</span>
                </div>
                <Badge variant="secondary">Active</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-medium">Payment Link</span>
                </div>
                <Badge variant="secondary">Active</Badge>
              </div>
              
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="h-4 w-4" />
                  <span>Templates are automatically integrated with event flows</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-yellow-200 bg-yellow-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            Important Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div>
                <strong>React Components:</strong> Use existing React Email components or create new ones
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div>
                <strong>Handlebars Variables:</strong> Use <code className="bg-white px-1 rounded">{`{{variable_name}}`}</code> syntax for dynamic content
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div>
                <strong>Sample Data:</strong> Provide sample data for template testing and preview
              </div>
            </div>
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div>
                <strong>Template Registry:</strong> React components must be registered in the template renderer
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}